import { useState } from 'react';
import type { Address, Hash } from 'viem';
import { useWalletClient } from 'wagmi';

import type { TransactionQueueItem } from 'components';
import MAX_UINT256 from 'constants/maxUint256';
import {
  getContractAddress,
  isolatedPoolComptrollerAbi,
  positionRouterAbi,
  vBep20Abi,
} from 'libs/contracts';
import { stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { STAKING_V2_ADDRESS } from 'libs/contracts/config/bittensorPrecompiles';
import { wrapPositionRouterError } from 'libs/errors';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';

import { getVTokenWrapperKind } from '../shared/getVTokenWrapperKind';
import {
  failedStepStateReducer,
  getIsRouterDelegated,
  getRouterQueueStep,
  getStakingAllowance,
  runRouterQueueStep,
} from '../shared/routerQueueCore';
import { invalidateRouterQueries } from '../shared/routerQueueInvalidation';
import {
  calculateSlippageFloor,
  computeNativeOutFloor,
  computeRouterDeadline,
  computeWrappedOutFloor,
  floorToRao,
} from '../shared/routerSlippage';
import type { BittensorNativeSupplySource } from './getBittensorSupplySourceTokens';

export interface RouterOpenPositionQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
}

export type OpenPositionSupplySource =
  | { kind: 'native-tao' }
  | { kind: 'native-alpha'; source: BittensorNativeSupplySource };

interface ExecuteRouterOpenPositionQueueInput {
  supplyAmountMantissa: bigint;
  supplySource: OpenPositionSupplySource;
  supplyVToken: VToken;
  borrowAmountMantissa: bigint;
  borrowVToken: VToken;
  borrowUnwrapNative: boolean;
  slippagePercentage: number;
}

export const useRouterOpenPositionQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = ({
    action,
    ...step
  }: RouterOpenPositionQueuePlanStep & { action: () => Promise<Hash> }) =>
    runRouterQueueStep({ step, action, updateItem, publicClient });

  const execute = async ({
    supplyAmountMantissa,
    supplySource,
    supplyVToken,
    borrowAmountMantissa,
    borrowVToken,
    borrowUnwrapNative,
    slippagePercentage,
  }: ExecuteRouterOpenPositionQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    const routerAddress = getContractAddress({ name: 'PositionRouter', chainId });
    const poolComptrollerAddress = getContractAddress({
      name: 'LegacyPoolComptroller',
      chainId,
    });

    if (!routerAddress || !poolComptrollerAddress) {
      throw new Error('PositionRouter or LegacyPoolComptroller address not configured');
    }

    setItems([]);
    setIsRunning(true);

    try {
      const supplySymbol = supplyVToken.underlyingToken.symbol;
      const borrowSymbol = borrowVToken.underlyingToken.symbol;

      const isDelegated = await getIsRouterDelegated({
        accountAddress,
        publicClient,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
      });

      let stakingAllowance = 0n;
      if (supplySource.kind === 'native-alpha') {
        stakingAllowance = await getStakingAllowance({
          accountAddress,
          publicClient,
          routerAddress: routerAddress as Address,
          netuid: supplySource.source.netuid,
        });
      }

      const setupSteps: RouterOpenPositionQueuePlanStep[] = [];
      if (supplySource.kind === 'native-alpha') {
        setupSteps.push({
          id: 'approve-native-alpha',
          label: 'Approve native alpha spend',
          shouldSkip: stakingAllowance >= supplyAmountMantissa,
          status: 'idle',
        });
      }

      const plan: RouterOpenPositionQueuePlanStep[] = [
        {
          id: 'approve-delegate',
          label: 'Approve PositionRouter as delegate',
          shouldSkip: !!isDelegated,
          status: 'idle',
        },
        ...setupSteps,
        {
          id: 'router-open-position',
          label: `Supply ${supplySymbol} + borrow ${borrowSymbol}`,
          shouldSkip: false,
          status: 'idle',
        },
      ];
      setItems(plan);

      await runStep({
        ...getRouterQueueStep(plan, 'approve-delegate'),
        action: () =>
          walletClient.writeContract({
            address: poolComptrollerAddress as Address,
            abi: isolatedPoolComptrollerAbi,
            functionName: 'updateDelegate',
            args: [routerAddress as Address, true],
            account: accountAddress,
          }),
      });

      if (supplySource.kind === 'native-alpha') {
        await runStep({
          ...getRouterQueueStep(plan, 'approve-native-alpha'),
          action: () =>
            walletClient.writeContract({
              address: STAKING_V2_ADDRESS as Address,
              abi: stakingV2Abi,
              functionName: 'approve',
              args: [
                routerAddress as Address,
                supplySource.source.netuid,
                BigInt(MAX_UINT256.toFixed()),
              ],
              account: accountAddress,
            }),
        });
      }

      let supplyMinSharesOut: bigint;
      let effectiveSupplyAmount = supplyAmountMantissa;
      if (supplySource.kind === 'native-tao') {
        effectiveSupplyAmount = floorToRao(supplyAmountMantissa);
        if (effectiveSupplyAmount === 0n) {
          throw new Error('TAO supply amount must be at least 1 RAO (1e-9 TAO)');
        }
        const wsTaoRate = await publicClient.readContract({
          address: supplyVToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        supplyMinSharesOut = computeWrappedOutFloor({
          nativeAmount: effectiveSupplyAmount,
          exchangeRate: wsTaoRate,
          slippagePercentage,
        });
      } else {
        // Read wAlpha.exchangeRate() live — rate updates with every Subtensor
        // block, so a cached rate would drift during the wallet-confirm window.
        const wAlphaSupplyRate = await publicClient.readContract({
          address: supplyVToken.underlyingToken.address,
          abi: wAlphaAbi,
          functionName: 'exchangeRate',
        });
        supplyMinSharesOut = computeWrappedOutFloor({
          nativeAmount: supplyAmountMantissa,
          exchangeRate: wAlphaSupplyRate,
          slippagePercentage,
        });
      }

      const borrowAvailableCash = await publicClient.readContract({
        address: borrowVToken.address,
        abi: vBep20Abi,
        functionName: 'getCash',
      });
      if (borrowAmountMantissa > borrowAvailableCash) {
        throw new Error(
          `Bundled borrow exceeds available ${borrowSymbol} liquidity. Market has ${borrowAvailableCash} base units available, requested ${borrowAmountMantissa}.`,
        );
      }

      // BorrowLeg.minOut is dispatch-dependent: when unwrap-to-native is set,
      // it's compared against the post-unwrap native output, so rescale via
      // exchangeRate() before applying the slippage percentage. Without the
      // rescale the alpha path reverts on every call (1e9× misscale).
      const borrowWrapperKind = await getVTokenWrapperKind({
        publicClient,
        underlyingAddress: borrowVToken.underlyingToken.address,
      });
      if (borrowUnwrapNative && borrowWrapperKind === 'unsupported') {
        throw new Error(
          `Open-position bundled borrow leg is not supported for ${borrowSymbol}: underlying is neither a wsTAO nor a wAlpha wrapper.`,
        );
      }
      const isWsTAOBorrow = borrowWrapperKind === 'wsTAO';
      let borrowMinOut: bigint;
      if (borrowUnwrapNative && isWsTAOBorrow) {
        const wsTaoBorrowRate = await publicClient.readContract({
          address: borrowVToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        borrowMinOut = computeNativeOutFloor({
          wrapperAmount: borrowAmountMantissa,
          exchangeRate: wsTaoBorrowRate,
          slippagePercentage,
        });
      } else if (borrowUnwrapNative && !isWsTAOBorrow) {
        const wAlphaBorrowRate = await publicClient.readContract({
          address: borrowVToken.underlyingToken.address,
          abi: wAlphaAbi,
          functionName: 'exchangeRate',
        });
        borrowMinOut = computeNativeOutFloor({
          wrapperAmount: borrowAmountMantissa,
          exchangeRate: wAlphaBorrowRate,
          slippagePercentage,
        });
      } else {
        borrowMinOut = calculateSlippageFloor({
          amount: borrowAmountMantissa,
          slippagePercentage,
        });
      }

      const deadline = computeRouterDeadline();
      const finalStep = getRouterQueueStep(plan, 'router-open-position');

      await runStep({
        ...finalStep,
        action: () => {
          const supplyLeg = {
            vToken: supplyVToken.address,
            amount: supplySource.kind === 'native-tao' ? 0n : effectiveSupplyAmount,
            isNative: supplySource.kind === 'native-tao',
            minSharesOut: supplyMinSharesOut,
          } as const;
          const borrowLeg = {
            vToken: borrowVToken.address,
            amount: borrowAmountMantissa,
            unwrapNative: borrowUnwrapNative,
            minOut: borrowMinOut,
          } as const;

          if (supplySource.kind === 'native-tao') {
            return walletClient.writeContract({
              address: routerAddress as Address,
              abi: positionRouterAbi,
              functionName: 'openPosition',
              args: [supplyLeg, borrowLeg, deadline],
              value: effectiveSupplyAmount,
              account: accountAddress,
            });
          }
          return walletClient.writeContract({
            address: routerAddress as Address,
            abi: positionRouterAbi,
            functionName: 'openPosition',
            args: [supplyLeg, borrowLeg, deadline],
            account: accountAddress,
          });
        },
      });

      invalidateRouterQueries({
        accountAddress,
        chainId,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
        vTokenAddresses: [supplyVToken.address, borrowVToken.address],
        netuid: supplySource.kind === 'native-alpha' ? supplySource.source.netuid : undefined,
      });
    } catch (error) {
      setItems(failedStepStateReducer);
      throw wrapPositionRouterError(error);
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
